import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { CheckCircle2, AlertCircle, Upload, Download, Loader2, AlertTriangle } from "lucide-react";

type PeopleImportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const EMPLOYEE_COLUMNS = ['fullName', 'jobTitle', 'email'];

const getCellValue = (row: any, aliases: string[]) => {
  for (const alias of aliases) {
    if (row[alias] !== undefined) return row[alias];
  }
  return '';
};

export default function PeopleImportDialog({
  open,
  onOpenChange,
}: PeopleImportDialogProps) {
  const [step, setStep] = useState<'upload' | 'validate' | 'importing' | 'done'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const reset = () => {
    setStep('upload');
    setFile(null);
    setPreviewData([]);
    setErrors([]);
    setImportProgress(0);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const parseCSV = (text: string): any[] => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) throw new Error("CSV must have a header row and at least one data row");
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    return lines.slice(1).map((line) => {
      const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const row: any = {};
      headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
      row.fullName = row.fullName || getCellValue(row, ['Full Name', 'Employee Name', 'employeeName', 'name', 'Name']);
      row.jobTitle = row.jobTitle || getCellValue(row, ['Job Title', 'title', 'Title']);
      row.email = row.email || getCellValue(row, ['Email', 'Email Address', 'employeeEmail']);
      return row;
    });
  };

  const validate = (data: any[]): string[] => {
    const errs: string[] = [];
    if (data.length === 0) { errs.push("No data rows found."); return errs; }
    const emails = new Set<string>();
    data.forEach((row, i) => {
      const rowNum = i + 2;
      const fullName = getCellValue(row, ['fullName', 'Full Name', 'Employee Name', 'employeeName', 'name', 'Name']);
      const email = getCellValue(row, ['email', 'Email', 'Email Address', 'employeeEmail']);

      if (!fullName?.trim()) errs.push(`Row ${rowNum}: employee name is required`);
      if (!email?.trim()) {
        errs.push(`Row ${rowNum}: email is required`);
      } else {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
          errs.push(`Row ${rowNum}: invalid email "${email}"`);
        }
        if (emails.has(email.toLowerCase())) {
          errs.push(`Row ${rowNum}: duplicate email "${email}"`);
        }
        emails.add(email.toLowerCase());
      }
    });
    return errs;
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.endsWith('.csv')) {
      toast({ title: "Invalid file", description: "Please upload a CSV file.", variant: "destructive" });
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = parseCSV(ev.target?.result as string);
        const errs = validate(data);
        setPreviewData(data);
        setErrors(errs);
        setStep('validate');
      } catch (err: any) {
        toast({ title: "Error parsing CSV", description: err.message, variant: "destructive" });
      }
    };
    reader.readAsText(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setStep('importing');
    setImportProgress(20);
    try {
      const formData = new FormData();
      formData.append('file', file);
      setImportProgress(50);
      const res = await fetch('/api/employees/import', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      setImportProgress(80);
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || result.error || 'Import failed');
      setImportResult(result);
      setImportProgress(100);
      setStep('done');
      queryClient.invalidateQueries({ queryKey: ['/api/employees'] });
    } catch (err: any) {
      toast({ title: "Import failed", description: err.message, variant: "destructive" });
      setStep('validate');
      setImportProgress(0);
    }
  };

  const downloadTemplate = () => {
    const csv = [
      "Employee Name,Job Title,Email",
      '"John Doe","Software Engineer","john.doe@company.com"',
      '"Jane Smith","Product Manager","jane.smith@company.com"',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Template downloaded" });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) reset(); onOpenChange(isOpen); }}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 'upload' && 'Import Employees'}
            {step === 'validate' && 'Review Employee Data'}
            {step === 'importing' && 'Importing Employees...'}
            {step === 'done' && 'Import Complete'}
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Upload a CSV file with employee details.'}
            {step === 'validate' && 'Check your data before importing.'}
            {step === 'importing' && 'Please wait while we import your employees.'}
            {step === 'done' && 'Employees have been imported successfully.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center space-y-3">
                <div className="rounded-full bg-primary/10 p-3">
                  <Upload className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-medium text-lg">Click to upload CSV</h3>
                <p className="text-sm text-slate-500">CSV files only (max 10MB)</p>
              </div>
            </div>

            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Required columns</AlertTitle>
              <AlertDescription className="text-amber-700 text-sm">
                <strong>Employee Name</strong> and <strong>Email</strong> are required.
                Optional: <em>Job Title</em>
              </AlertDescription>
            </Alert>

            <Button variant="outline" className="w-full" onClick={downloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Download Employee Template
            </Button>

            <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFile} className="hidden" />
          </div>
        )}

        {step === 'validate' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-md border bg-slate-50">
              <Badge>{previewData.length} rows found</Badge>
              {errors.length === 0 ? (
                <span className="flex items-center text-green-600 text-sm">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Ready to import
                </span>
              ) : (
                <span className="flex items-center text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4 mr-1" /> {errors.length} issue(s) found
                </span>
              )}
            </div>

            {errors.length > 0 && (
              <div className="border border-red-200 rounded-md p-4 bg-red-50 max-h-40 overflow-y-auto">
                <h4 className="font-medium text-red-700 mb-2">Validation Errors</h4>
                {errors.map((e, i) => (
                  <p key={i} className="text-sm text-red-600">{e}</p>
                ))}
              </div>
            )}

            {previewData.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 text-sm">Preview (first 5 rows)</h4>
                <div className="max-h-[220px] overflow-auto border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        {EMPLOYEE_COLUMNS
                          .filter(h => previewData[0]?.[h] !== undefined)
                          .map(h => (
                            <th key={h} className="p-2 text-left text-xs font-medium text-slate-500 uppercase border-b">{h}</th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-b">
                          {EMPLOYEE_COLUMNS
                            .filter(h => previewData[0]?.[h] !== undefined)
                            .map(h => (
                              <td key={h} className="p-2 text-slate-600">{row[h] || '-'}</td>
                            ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'importing' && (
          <div className="py-8 space-y-4 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <Progress value={importProgress} className="h-2" />
            <p className="text-sm text-slate-600">Importing employees, please wait...</p>
          </div>
        )}

        {step === 'done' && importResult && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-green-50 rounded-md">
                <p className="text-2xl font-bold text-green-600">{importResult.results?.success ?? 0}</p>
                <p className="text-xs text-green-700">Imported</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-md">
                <p className="text-2xl font-bold text-yellow-600">{importResult.results?.skipped ?? 0}</p>
                <p className="text-xs text-yellow-700">Skipped</p>
              </div>
              <div className="p-3 bg-red-50 rounded-md">
                <p className="text-2xl font-bold text-red-600">{importResult.results?.errors ?? 0}</p>
                <p className="text-xs text-red-700">Errors</p>
              </div>
            </div>
            {importResult.results?.errorDetails?.length > 0 && (
              <div className="border border-red-200 rounded-md p-3 bg-red-50 max-h-32 overflow-y-auto">
                {importResult.results.errorDetails.map((d: any, i: number) => (
                  <p key={i} className="text-xs text-red-600">Row {d.row}: {d.error}</p>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step !== 'importing' && (
            <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>
              {step === 'done' ? 'Close' : 'Cancel'}
            </Button>
          )}
          {step === 'validate' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')}>Back</Button>
              <Button onClick={handleImport} disabled={errors.length > 0}>
                Import {previewData.length} Employees
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
